import { useStore } from '@nanostores/react';
import { addMonths, startOfMonth } from 'date-fns';
import { memo, useCallback, useMemo, useState } from 'react';
import { TokenUsageHeader } from './Header';
import { TokenUsageCard } from './TokenUsageCard';
import { SectionContainer } from '~/components/dialogs/settings/SectionContainer';
import { FreePlanPricingSettingsSection } from '~/components/ui/pricing/SettingsSection/FreePlan';
import { ProPlanPricingSettingsSection } from '~/components/ui/pricing/SettingsSection/ProPlan';
import { TeamPlanPricingSettingsSection } from '~/components/ui/pricing/SettingsSection/TeamPlan';
import type { TokenStats } from '~/lib/.server/rateLimit';
import { chatStore } from '~/lib/stores/chat';
import { redirectToPersonalBillingPage } from '~/lib/stores/pricing/helpers';
import { userStore } from '~/lib/stores/user';
import type { OrganizationData } from '~/types/organization';
import type { EffectiveTier } from '~/utils/effectiveTier';
import {
  computeAllTokensLeft,
  computeReferralTokensAggregate,
  computeTokensLeftForBalance,
} from '~/utils/tokens-stats';
import { withSpinner } from '~/utils/withSpinner';

interface Props {
  tier?: EffectiveTier;
  activeOrganization?: OrganizationData | null;
  closeDialog?: VoidFunction;
}

interface TokenCalculations {
  tokensLeft: number;
  remainingSpecialTokens: number;
  remainingReferralTokens: number;
  remainingPurchasedTokens: number;
  remainingDailyTokens: number;
  maxDailyTokens: number;
  regularTokenBreakdown: TokenStats['regularTokenBreakdown'];
}

export const TierTokenUsageDetails = memo(({ tier, closeDialog, activeOrganization }: Props) => {
  const tokenStats = useStore(chatStore.tokenStats);
  const user = useStore(userStore.user);

  const isProTier = tier?.plan === 'pro';
  const isTeamTier = tier?.plan === 'teams';
  const isFreeTier = tier?.plan === 'personal';
  const isTeamMember = isTeamTier && activeOrganization?.role === 'member';

  const teamSubscription = activeOrganization?.billing?.subscription;
  const hasSubscriptionInfo = isTeamTier ? !!teamSubscription : !!user?.membership?.subscription;

  const [isRedirecting, setIsRedirecting] = useState(false);

  // calculate billing dates
  const individualBillingDate = isProTier ? tokenStats?.billingPeriod?.to : startOfMonth(addMonths(new Date(), 1));
  const teamBillingDate = isTeamTier && activeOrganization?.billing?.subscription?.nextMonthStartTimestamp;
  const billingDate = isTeamTier ? teamBillingDate || undefined : individualBillingDate;

  // determine if user can manage billing
  const canManageBilling = useMemo(() => {
    if (!isTeamTier) {
      return hasSubscriptionInfo;
    }

    return activeOrganization?.role === 'admin' && hasSubscriptionInfo;
  }, [isTeamTier, isTeamMember, hasSubscriptionInfo]);

  const tokenCalculations: TokenCalculations = useMemo(() => {
    const referralTokens = computeReferralTokensAggregate(tokenStats, tier);
    const remainingReferralTokens = computeTokensLeftForBalance(referralTokens);
    const remainingPurchasedTokens = computeTokensLeftForBalance(tokenStats?.purchased);
    const remainingSpecialTokens = computeTokensLeftForBalance(tokenStats?.specialTokens);

    let tokensLeft = computeAllTokensLeft(tokenStats, tier);

    const regularTokenBreakdown = tokenStats?.regularTokenBreakdown || [];
    const usedDailyTokens = tokenStats?.totalToday ?? 0;
    const maxDailyTokens = tokenStats?.maxPerDay ?? 0;
    const remainingDailyTokens = maxDailyTokens - usedDailyTokens;

    /**
     * TODO: remove this after backend fix.
     * https://linear.app/stackblitz/issue/BAC-145/extend-member-data-or-create-new-endpoint-to-fetch-member-info .
     */
    if (isTeamTier && regularTokenBreakdown.length === 0 && !!tokenStats?.maxPerMonth && tokenStats.billingPeriod?.to) {
      regularTokenBreakdown.push({
        available: tokenStats.maxPerMonth,
        used: tokenStats.totalThisMonth,
        expiresOn: addMonths(tokenStats.billingPeriod.to, 1).valueOf(),
        type: 'active-month',
      });

      tokensLeft = tokenStats.maxPerMonth - tokenStats.totalThisMonth;
    }

    return {
      remainingReferralTokens,
      remainingPurchasedTokens,
      remainingSpecialTokens,
      regularTokenBreakdown,
      remainingDailyTokens,
      maxDailyTokens,
      tokensLeft,
    };
  }, [tokenStats, tier]);

  const onManageBillingClick = useCallback(() => {
    withSpinner({
      action: async () => {
        await redirectToPersonalBillingPage();
      },
      onStateUpdate: setIsRedirecting,
    });
  }, []);

  // determine account title based on tier and type
  let accountTitle = 'Free plan';

  if (isTeamTier) {
    accountTitle = 'Teams plan';
  } else if (isProTier) {
    accountTitle = 'Pro plan';
  }

  // render pricing section based on plan type
  let pricingSection = null;

  if (!user) {
    pricingSection = null;
  } else if (isTeamTier && activeOrganization && closeDialog) {
    pricingSection = (
      <TeamPlanPricingSettingsSection organization={activeOrganization} closeSettingsDialog={closeDialog} />
    );
  } else if (isProTier && closeDialog) {
    pricingSection = <ProPlanPricingSettingsSection user={user} closeSettingsDialog={closeDialog} />;
  } else if (isFreeTier) {
    pricingSection = <FreePlanPricingSettingsSection />;
  }

  return (
    <SectionContainer
      title={
        <TokenUsageHeader
          tokens={tokenStats?.maxPerMonth}
          billingDate={billingDate}
          canManageBilling={canManageBilling}
          onManageBilling={onManageBillingClick}
          isLoading={isRedirecting}
          isTeam={isTeamTier}
        />
      }
      className="gap-8"
    >
      <TokenUsageCard
        className="mt-4"
        account={{
          title: accountTitle,
          description: user?.name || user?.email,
          accentLine: 'second',
          user,
        }}
        tokensLeft={tokenCalculations.tokensLeft}
        specialTokens={isFreeTier ? undefined : tokenCalculations.remainingSpecialTokens}
        referralTokens={tokenCalculations.remainingReferralTokens}
        purchasedTokens={tokenCalculations.remainingPurchasedTokens}
        isFreeTier={isFreeTier}
        regularTokenBreakdown={tokenCalculations.regularTokenBreakdown}
        remainingDailyTokens={tokenCalculations.remainingDailyTokens}
        maxDailyTokens={tokenCalculations.maxDailyTokens}
      />

      {pricingSection}
    </SectionContainer>
  );
});
